<template>
  <div class="max-w-2xl mx-auto">
    <form @submit.prevent="saveMasterBoard" class="space-y-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Master Board Name *</label>
        <input v-model="form.name" type="text" required
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder="Enter master board name" />
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea v-model="form.description" rows="3"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder="Enter master board description"></textarea>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Members</label>
        <multiselect v-model="selectedMembers" :options="memberOptions" :multiple="true" :close-on-select="false"
          :clear-on-select="false" :preserve-search="true" :internal-search="false"
          placeholder="Select master board members" label="name" track-by="id" :loading="membersLoading"
          :custom-label="memberLabel" @search-change="onMemberSearchChange" class="w-full">
          <template #option="{ option }">
            <div class="flex flex-col py-1">
              <span class="text-sm font-medium text-gray-900">{{ option.name }}</span>
              <span class="text-xs text-gray-500">{{ option.email }}</span>
            </div>
          </template>
          <template #noResult>
            <span class="text-sm text-gray-500">No users found.</span>
          </template>
        </multiselect>
        <p class="mt-2 text-xs text-gray-500">
          The creator will always remain a member (no duplicates).
        </p>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Color</label>
        <div class="flex gap-2">
          <div v-for="color in colors" :key="color" @click="form.color = color"
            class="w-10 h-10 rounded-lg cursor-pointer border-2 transition-all" :style="{ backgroundColor: color }"
            :class="{ 'border-gray-900 scale-110': form.color === color, 'border-transparent': form.color !== color }">
          </div>
        </div>
      </div>

      <div class="attachments-section">
        <div class="attachments-header">
          <div class="attachments-icon">
            <icon-paper-clip class="h-5 w-5"></icon-paper-clip>
          </div>
          <div>
            <h3 class="attachments-title">Attachments</h3>
            <p class="attachments-subtitle">Max 2 MB per file, 50 MB total · JPG, PNG, GIF, PDF, DOC, DOCX, XLS, XLSX,
              PPT, PPTX, TXT, CSV</p>
          </div>
        </div>
        <div class="attachments-list">
          <!-- Existing attachments -->
          <div v-for="(att, index) in existingAttachments" :key="'existing-' + att.id"
            class="attachment-row attachment-row-existing">
            <div class="attachment-badge">{{ index + 1 }}</div>
            <div class="attachment-col attachment-col-name">
              <label class="attachment-label">Document Name</label>
              <span class="attachment-existing-name">{{ att.display_name || att.original_filename }}</span>
            </div>
            <div class="attachment-col attachment-col-file">
              <label class="attachment-label">File</label>
              <div class="file-selected-preview file-existing-preview">
                <icon-document class="h-5 w-5 text-slate-500 flex-shrink-0"></icon-document>
                <span class="file-selected-name">{{ att.original_filename }}</span>
                <span class="file-selected-size">{{ formatFileSize(att.file_size) }}</span>
                <button type="button" @click="viewExistingAttachment(att)" class="attachment-download-btn" title="View">
                  <icon-eye class="h-4 w-4"></icon-eye>
                </button>
                <button type="button" @click="downloadExistingAttachment(att)" class="attachment-download-btn"
                  title="Download">
                  <icon-download class="h-4 w-4"></icon-download>
                </button>
                <button type="button" @click="deleteExistingAttachment(att)"
                  class="attachment-download-btn attachment-delete-btn" title="Delete">
                  <icon-trash class="h-4 w-4"></icon-trash>
                </button>
              </div>
            </div>
            <div class="attachment-actions"></div>
          </div>
          <!-- New attachments (for adding - not persisted on update) -->
          <div v-for="(item, index) in inputAttachments" :key="'new-' + index" class="attachment-row"
            :class="{ 'attachment-row-has-file': item.document && item.document.val }">
            <div class="attachment-badge">{{ existingAttachments.length + index + 1 }}</div>
            <div class="attachment-col attachment-col-name">
              <label :for="'attachment-name-' + index" class="attachment-label">Document Name</label>
              <input :id="'attachment-name-' + index" v-model.trim="item.file_name" type="text" class="attachment-input"
                placeholder="Enter document name" />
            </div>
            <div class="attachment-col attachment-col-file">
              <label :for="'attachment-file-' + index" class="attachment-label">File</label>
              <div class="file-upload-area">
                <input :id="'attachment-file-' + index" :key="'attachment-file-' + index" type="file"
                  class="file-input-hidden" :accept="fileAccept" @change="handleFileChange" />
                <div v-if="item.document && item.document.val" class="file-selected-preview">
                  <icon-document class="h-5 w-5 text-slate-500 flex-shrink-0"></icon-document>
                  <span class="file-selected-name">{{ item.document.val.name }}</span>
                  <span class="file-selected-size">{{ formatFileSize(item.document.val.size) }}</span>
                </div>
                <label v-else :for="'attachment-file-' + index" class="file-upload-placeholder">
                  <icon-paper-clip class="h-5 w-5 text-slate-400"></icon-paper-clip>
                  <span>Choose file or drag here</span>
                  <span class="file-upload-hint">max 2 MB</span>
                </label>
              </div>
              <transition name="fade" mode="out-in">
                <p v-if="!item.document.isValid" class="attachment-error">{{ item.document.msg }}</p>
              </transition>
            </div>
            <div class="attachment-actions">
              <button v-if="showAddMoreAttachmentBtn(index)" type="button" @click="addMoreAttachment"
                class="action-btn action-btn-add" title="Add another file">
                <icon-plus class="h-4 w-4"></icon-plus>
              </button>
              <button v-if="inputAttachments.length > 1" type="button" @click="removeAttachment(index)"
                class="action-btn action-btn-remove" title="Remove">
                <icon-minus class="h-4 w-4"></icon-minus>
              </button>
            </div>
          </div>
        </div>
        <div v-if="totalFileSize > 0 || existingAttachments.length > 0" class="attachments-footer">
          <div class="attachments-usage">
            <div class="attachments-usage-bar" :style="{ width: totalUsagePercent + '%' }"></div>
          </div>
          <span class="attachments-usage-text">{{ formatFileSize(totalFileSize + existingTotalSize) }} / 50 MB</span>
        </div>
      </div>

      <div class="flex gap-3 pt-4">
        <button type="button" @click="$emit('close')"
          class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button type="submit" :disabled="loading"
          class="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          <span v-if="!loading">Save</span>
          <span v-else>Saving...</span>
        </button>
      </div>
    </form>

    <!-- Attachment Preview Modal -->
    <base-modal v-if="showPreviewModal" :title="previewModalTitle" width="90vw" height="85vh"
      @close="closePreviewModal">
      <div class="preview-modal-wrapper">
        <div class="preview-modal-content">
          <div v-if="previewLoading" class="preview-loading">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p class="text-gray-600">Loading preview...</p>
          </div>
          <div v-else-if="previewError" class="preview-error">
            <p class="text-red-600 mb-4">{{ previewError }}</p>
            <button type="button" @click="downloadExistingAttachment(previewAttachment)"
              class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600">
              Download Instead
            </button>
          </div>
          <div v-else-if="previewAttachment && previewUrl" class="preview-body">
            <div v-if="isPdfFile(previewAttachment.file_type, previewAttachment.original_filename)"
              class="preview-iframe-wrap">
              <iframe :src="previewUrl" class="preview-iframe" frameborder="0"></iframe>
            </div>
            <div v-else-if="isImageFile(previewAttachment.file_type, previewAttachment.original_filename)"
              class="preview-image-wrap">
              <img :src="previewUrl" :alt="previewAttachment.original_filename" class="preview-image" />
            </div>
            <div v-else class="preview-fallback">
              <p class="text-gray-600 mb-4">Preview not available for this file type.</p>
              <p class="text-gray-500 mb-4">Please download to view the content.</p>
              <button type="button" @click="downloadExistingAttachment(previewAttachment)"
                class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600">
                Download File
              </button>
            </div>
          </div>
        </div>
        <div v-if="!previewLoading && !previewError && previewAttachment" class="preview-footer">
          <button type="button" @click="downloadExistingAttachment(previewAttachment)"
            class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 flex items-center gap-2">
            <icon-download class="h-5 w-5"></icon-download>
            Download
          </button>
        </div>
      </div>
    </base-modal>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import Multiselect from 'vue-multiselect';
import IconPlus from '../../../components/icons/IconPlus.vue';
import IconMinus from '../../../components/icons/IconMinus.vue';
import IconPaperClip from '../../../components/icons/IconPaperClip.vue';
import IconDocument from '../../../components/icons/IconDocument.vue';
import IconDownload from '../../../components/icons/IconDownload.vue';
import IconEye from '../../../components/icons/IconEye.vue';
import IconTrash from '../../../components/icons/IconTrash.vue';
import BaseModal from '../../../components/ui/BaseModal.vue';
import 'vue-multiselect/dist/vue-multiselect.css';

export default {
  name: 'edit-master-board-modal',
  components: {
    Multiselect,
    IconPlus,
    IconMinus,
    IconPaperClip,
    IconDocument,
    IconDownload,
    IconEye,
    IconTrash,
    BaseModal,
  },
  props: {
    masterBoard: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      form: {
        name: '',
        description: '',
        color: '#499DD4',
      },
      colors: ['#499DD4', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'],
      memberOptions: [],
      selectedMembers: [],
      membersLoading: false,
      inputAttachments: [
        {
          file_name: '',
          document: { val: null, isValid: true, msg: '' },
        },
      ],
      fileAccept: '.jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv',
      allowedExtensions: ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv'],
      showPreviewModal: false,
      previewAttachment: null,
      previewUrl: null,
      previewLoading: false,
      previewError: null,
      deletedAttachmentIds: [],
    };
  },
  computed: {
    existingAttachments() {
      const atts = this.masterBoard && this.masterBoard.attachments;
      if (!atts || !Array.isArray(atts)) return [];
      return atts.filter(a => this.deletedAttachmentIds.indexOf(a.id) === -1);
    },
    existingTotalSize() {
      return this.existingAttachments.reduce((sum, att) => sum + (att.file_size || 0), 0);
    },
    totalFileSize() {
      return this.inputAttachments.reduce((sum, item) => {
        const file = item.document && item.document.val;
        return sum + (file && file.size ? file.size : 0);
      }, 0);
    },
    totalUsagePercent() {
      const total = this.totalFileSize + this.existingTotalSize;
      const max = 50 * 1024 * 1024;
      return Math.min(100, Math.round((total / max) * 100));
    },
    previewModalTitle() {
      const att = this.previewAttachment;
      return att ? (att.display_name || att.original_filename || 'Attachment Preview') : 'Attachment Preview';
    },
  },
  mounted() {
    this.form.name = (this.masterBoard && this.masterBoard.name) ? this.masterBoard.name : '';
    this.form.description = (this.masterBoard && this.masterBoard.description) ? this.masterBoard.description : '';
    this.form.color = (this.masterBoard && this.masterBoard.color) ? this.masterBoard.color : '#499DD4';

    if (this.masterBoard && this.masterBoard.members && Array.isArray(this.masterBoard.members)) {
      this.selectedMembers = this.masterBoard.members.slice();
    }

    this.fetchMemberCandidates('');
  },
  methods: {
    memberLabel(option) {
      if (!option) return '';
      const name = option.name || '';
      const email = option.email || '';
      if (email) return `${name} (${email})`;
      return name;
    },
    async fetchMemberCandidates(search) {
      this.membersLoading = true;
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query TaskBoardMemberCandidates($search: String, $limit: Int) {
              taskBoardMemberCandidates(search: $search, limit: $limit) {
                error
                error_code
                message
                data {
                  id
                  name
                  email
                }
              }
            }
          `,
          variables: {
            search: search || null,
            limit: 30,
          },
          fetchPolicy: 'network-only',
        });

        const payload = data && data.taskBoardMemberCandidates;
        if (payload && payload.error) {
          this.memberOptions = [];
        } else {
          this.memberOptions = (payload && payload.data) ? payload.data : [];
        }

        if (this.selectedMembers && this.selectedMembers.length > 0) {
          const selectedById = {};
          this.selectedMembers.forEach(u => { selectedById[u.id] = u; });
          this.memberOptions.forEach(u => { selectedById[u.id] = u; });
          this.memberOptions = Object.values(selectedById);
        }
      } catch (e) {
        this.memberOptions = this.memberOptions || [];
      } finally {
        this.membersLoading = false;
      }
    },
    onMemberSearchChange(search) {
      const s = (search || '').trim();
      if (s.length >= 2) {
        this.fetchMemberCandidates(s);
      } else {
        this.fetchMemberCandidates('');
      }
    },
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    getFileExtension(filename) {
      if (!filename) return '';
      const parts = filename.split('.');
      return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
    },
    addMoreAttachment() {
      this.inputAttachments.push({
        file_name: '',
        document: { val: null, isValid: true, msg: '' },
      });
    },
    removeAttachment(index) {
      this.inputAttachments.splice(index, 1);
      if (this.inputAttachments.length === 0) {
        this.inputAttachments.push({
          file_name: '',
          document: { val: null, isValid: true, msg: '' },
        });
      }
    },
    showAddMoreAttachmentBtn(index) {
      if (this.inputAttachments.length <= 1) return true;
      return index === this.inputAttachments.length - 1;
    },
    sanitizeLabel(str) {
      if (!str || typeof str !== 'string') return '';
      const noHtml = str.replace(/<[^>]*>/g, '');
      const noControl = noHtml.split('').filter(function (c) {
        const code = c.charCodeAt(0);
        return code >= 32 && code !== 127;
      }).join('');
      return noControl.substring(0, 255);
    },
    isPdfFile(fileType, filename) {
      if (fileType) {
        const t = String(fileType).toLowerCase();
        if (t === 'application/pdf' || t.indexOf('pdf') !== -1) return true;
      }
      if (filename) {
        const ext = String(filename).split('.').pop().toLowerCase();
        return ext === 'pdf';
      }
      return false;
    },
    isImageFile(fileType, filename) {
      if (fileType) {
        const t = String(fileType).toLowerCase();
        if (t.indexOf('image/') === 0 || t === 'image/jpeg' || t === 'image/png' || t === 'image/gif' || t === 'image/webp') return true;
      }
      if (filename) {
        const ext = String(filename).split('.').pop().toLowerCase();
        return ['jpg', 'jpeg', 'png', 'gif', 'webp'].indexOf(ext) !== -1;
      }
      return false;
    },
    async viewExistingAttachment(attachment) {
      this.previewAttachment = attachment;
      this.previewLoading = true;
      this.previewError = null;
      this.previewUrl = null;
      this.showPreviewModal = true;

      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation GetTaskMasterBoardAttachmentContent($id: ID!) {
              getTaskMasterBoardAttachmentContent(id: $id) {
                error
                error_code
                message
                data {
                  content
                  file_type
                  attachment {
                    id
                    original_filename
                  }
                }
              }
            }
          `,
          variables: { id: attachment.id },
        });

        const payload = data && data.getTaskMasterBoardAttachmentContent;
        if (payload && payload.error) {
          this.previewError = payload.message || 'Failed to load preview.';
          return;
        }

        const contentData = payload && payload.data;
        if (!contentData || !contentData.content) {
          this.previewError = 'Failed to load preview.';
          return;
        }

        const base64Content = contentData.content;
        const binaryString = atob(base64Content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const mimeType = contentData.file_type || 'application/octet-stream';
        const blob = new Blob([bytes], { type: mimeType });
        this.previewUrl = URL.createObjectURL(blob);
      } catch (error) {
        console.error('Error loading attachment for preview:', error);
        this.previewError = 'Failed to load preview. Please try again.';
      } finally {
        this.previewLoading = false;
      }
    },
    closePreviewModal() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
      }
      this.showPreviewModal = false;
      this.previewAttachment = null;
      this.previewUrl = null;
      this.previewError = null;
    },
    async downloadExistingAttachment(attachment) {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation GetTaskMasterBoardAttachmentContent($id: ID!) {
              getTaskMasterBoardAttachmentContent(id: $id) {
                error
                error_code
                message
                data {
                  content
                  file_type
                  attachment {
                    id
                    original_filename
                  }
                }
              }
            }
          `,
          variables: { id: attachment.id },
        });

        const payload = data && data.getTaskMasterBoardAttachmentContent;
        if (payload && payload.error) {
          alert(payload.message || 'Failed to download attachment.');
          return;
        }

        const contentData = payload && payload.data;
        if (!contentData || !contentData.content) {
          alert('Failed to download attachment.');
          return;
        }

        const base64Content = contentData.content;
        const binaryString = atob(base64Content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: contentData.file_type || 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = (contentData.attachment && contentData.attachment.original_filename) || attachment.original_filename || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading attachment:', error);
        alert('Failed to download attachment. Please try again.');
      }
    },
    async deleteExistingAttachment(attachment) {
      const result = await this.$swal.fire({
        title: 'Are you sure?',
        text: 'This attachment will be permanently deleted.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel',
      });
      if (!result.isConfirmed) return;

      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation DeleteTaskMasterBoardAttachment($id: ID!) {
              deleteTaskMasterBoardAttachment(id: $id) {
                error
                error_code
                message
              }
            }
          `,
          variables: { id: attachment.id },
        });

        const payload = data && data.deleteTaskMasterBoardAttachment;
        if (payload && payload.error) {
          this.$swal.fire({ icon: 'error', title: 'Oops...', text: payload.message || 'Failed to delete attachment.' });
          return;
        }

        this.deletedAttachmentIds.push(attachment.id);
        this.$swal.fire({ icon: 'success', title: 'Deleted', text: 'Attachment deleted successfully.', toast: true, position: 'top-end', showConfirmButton: false, timer: 2000 });
      } catch (error) {
        console.error('Error deleting attachment:', error);
        this.$swal.fire({ icon: 'error', title: 'Oops...', text: 'Failed to delete attachment. Please try again.' });
      }
    },
    handleFileChange(event) {
      const id = event.target && event.target.id;
      const fileIndex = id ? parseInt(id.split('-').pop(), 10) : 0;
      const targetIndex = isNaN(fileIndex) ? this.inputAttachments.length - 1 : fileIndex;

      if (targetIndex < 0 || targetIndex >= this.inputAttachments.length) return;

      this.inputAttachments[targetIndex].document.isValid = true;
      this.inputAttachments[targetIndex].document.msg = '';

      const file = event.target.files && event.target.files[0];
      if (!file) return;

      const maxPerFile = 2 * 1024 * 1024;
      const maxTotal = 50 * 1024 * 1024;
      const otherSize = this.inputAttachments.reduce((s, item, i) => {
        if (i === targetIndex) return s;
        const f = item.document && item.document.val;
        return s + (f && f.size ? f.size : 0);
      }, 0);

      if (file.size > maxPerFile) {
        this.inputAttachments[targetIndex].document.isValid = false;
        this.inputAttachments[targetIndex].document.msg = 'File size must be less than 2MB.';
        return;
      }
      if (otherSize + file.size > maxTotal) {
        this.inputAttachments[targetIndex].document.isValid = false;
        this.inputAttachments[targetIndex].document.msg = 'Total size would exceed 50 MB.';
        return;
      }
      const ext = this.getFileExtension(file.name);
      if (this.allowedExtensions.indexOf(ext) === -1) {
        this.inputAttachments[targetIndex].document.isValid = false;
        this.inputAttachments[targetIndex].document.msg = 'Allowed: ' + this.allowedExtensions.join(', ');
        return;
      }

      this.inputAttachments[targetIndex].document.val = file;
      this.inputAttachments[targetIndex].document.isValid = true;
      this.inputAttachments[targetIndex].document.msg = '';
    },
    async saveMasterBoard() {
      const rowsWithFiles = this.inputAttachments.filter(item => item.document && item.document.val);
      const maxPerFile = 2 * 1024 * 1024;
      const maxTotal = 50 * 1024 * 1024;
      if (rowsWithFiles.length > 0) {
        for (let i = 0; i < rowsWithFiles.length; i++) {
          const item = rowsWithFiles[i];
          const file = item.document && item.document.val;
          if (file && file.size > maxPerFile) {
            alert('File "' + file.name + '" exceeds 2 MB limit.');
            return;
          }
        }
        const totalWithExisting = this.totalFileSize + this.existingTotalSize;
        if (totalWithExisting > maxTotal) {
          alert('Total file size (existing + new) exceeds 50 MB limit.');
          return;
        }
      }

      this.loading = true;
      try {
        const attachments = rowsWithFiles.map(item => item.document.val);
        const attachmentLabels = rowsWithFiles.map(item => {
          const name = item.file_name && String(item.file_name).trim();
          return this.sanitizeLabel(name || '');
        });

        const input = {
          name: this.form.name,
          description: this.form.description,
          color: this.form.color,
        };

        if (this.selectedMembers && this.selectedMembers.length > 0) {
          input.member_ids = this.selectedMembers.map(u => u.id);
        } else {
          input.member_ids = [];
        }

        if (attachments.length > 0) {
          input.attachments = attachments;
          input.attachment_labels = attachmentLabels;
        }

        const mutateOptions = {
          mutation: gql`
            mutation UpdateTaskMasterBoard($id: ID!, $input: UpdateTaskMasterBoardInput!) {
              updateTaskMasterBoard(id: $id, input: $input) {
                error
                error_code
                message
                data {
                  id
                  name
                }
              }
            }
          `,
          variables: {
            id: this.masterBoard.id,
            input,
          },
        };

        if (attachments.length > 0) {
          mutateOptions.context = {
            headers: {
              'content-type': 'multipart/form-data',
            },
          };
        }

        const { data } = await this.$apollo.mutate(mutateOptions);

        if (data && data.updateTaskMasterBoard && data.updateTaskMasterBoard.error) {
          alert(data.updateTaskMasterBoard.message);
        } else {
          this.inputAttachments = [
            { file_name: '', document: { val: null, isValid: true, msg: '' } },
          ];
          this.$emit('saved');
        }
      } catch (error) {
        alert('Failed to update master board. Please try again.');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.attachments-section {
  padding: 1.25rem 1.5rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.05);
}

.attachments-header {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f1f5f9;
}

.attachments-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #212a3e 0%, #394867 100%);
  color: #fff;
  flex-shrink: 0;
}

.attachments-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.25rem 0;
  letter-spacing: -0.01em;
}

.attachments-subtitle {
  font-size: 0.75rem;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
}

.attachments-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.attachment-row {
  display: grid;
  grid-template-columns: 2rem 180px 1fr 3rem;
  align-items: start;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 10px;
  transition: border-color 0.2s, background 0.2s;
}

.attachment-row-has-file {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.attachment-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  border-radius: 0.5rem;
  background: #212a3e;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
}

.attachment-col {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
}

.attachment-col-file {
  min-width: 0;
  overflow: hidden;
}

@media (max-width: 640px) {
  .attachment-row {
    grid-template-columns: 2rem 1fr 3rem;
    grid-template-areas: "badge name actions" "badge file file";
  }

  .attachment-badge {
    grid-area: badge;
  }

  .attachment-col-name {
    grid-area: name;
    width: auto;
  }

  .attachment-col-file {
    grid-area: file;
  }

  .attachment-actions {
    grid-area: actions;
  }
}

.attachment-label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #475569;
}

.attachment-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #fff;
  color: #334155;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.attachment-input::placeholder {
  color: #94a3b8;
}

.attachment-input:focus {
  outline: none;
  border-color: #212a3e;
  box-shadow: 0 0 0 3px rgba(33, 42, 62, 0.1);
}

.file-upload-area {
  position: relative;
  min-height: 2.75rem;
}

.file-input-hidden {
  position: absolute;
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  z-index: -1;
}

.file-upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  width: 100%;
  min-height: 2.75rem;
  padding: 0.5rem 0.75rem;
  border: 1px dashed #cbd5e1;
  border-radius: 0.5rem;
  background: #fff;
  color: #64748b;
  font-size: 0.8125rem;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, color 0.2s;
}

.file-upload-placeholder:hover {
  border-color: #94a3b8;
  background: #f8fafc;
  color: #475569;
}

.file-upload-hint {
  font-size: 0.6875rem;
  color: #94a3b8;
}

.file-selected-preview {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  min-width: 0;
  overflow: hidden;
}

.file-selected-name {
  flex: 1;
  min-width: 0;
  font-size: 0.8125rem;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-selected-size {
  font-size: 0.6875rem;
  color: #94a3b8;
  flex-shrink: 0;
}

.attachment-existing-name {
  font-size: 0.875rem;
  color: #334155;
}

.attachment-download-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border-radius: 0.375rem;
  color: #64748b;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.2s, background 0.2s;
}

.attachment-download-btn:hover {
  color: #212a3e;
  background: #f1f5f9;
}

.attachment-delete-btn:hover {
  color: #dc2626;
  background: #fef2f2;
}

.file-existing-preview {
  cursor: default;
}

.attachment-error {
  font-size: 0.75rem;
  color: #dc2626;
  margin: 0.25rem 0 0 0;
}

.attachment-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex-shrink: 0;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn-add {
  background: #212a3e;
  color: #fff;
  border: 1px solid #212a3e;
}

.action-btn-add:hover {
  background: #394867;
  border-color: #394867;
}

.action-btn-remove {
  background: #fff;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.action-btn-remove:hover {
  background: #fef2f2;
}

.attachments-footer {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f1f5f9;
}

.attachments-usage {
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.attachments-usage-bar {
  height: 100%;
  background: linear-gradient(90deg, #212a3e 0%, #394867 100%);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.attachments-usage-text {
  font-size: 0.75rem;
  color: #64748b;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Preview Modal */
.preview-modal-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 400px;
}

.preview-modal-content {
  flex: 1;
  min-height: 300px;
}

.preview-loading,
.preview-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;
}

.preview-body {
  padding: 1rem;
}

.preview-iframe-wrap,
.preview-image-wrap {
  width: 100%;
  min-height: 400px;
}

.preview-iframe {
  width: 100%;
  height: 70vh;
  min-height: 400px;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
}

.preview-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 0.5rem;
}

.preview-fallback {
  text-align: center;
  padding: 3rem 1.5rem;
}

.preview-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}
</style>
